import React, { useState, useEffect, createContext } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Select,
    MenuItem,
    Chip,
    Typography
} from "@mui/material";
import {
    Language as WebsiteIcon,
    Search as SEOIcon
} from "@mui/icons-material";
import { getChildrenItems } from '../../../Stack/services/getChildrenItems';
import { QwantaProvider } from 'qwanyx';
import { DNDEditor } from '../../DNDEditor';

// Create a context for website data
export const WebsiteContext = createContext(null);

// Helper function for selection styling
const getSelectionStyle = (isSelected, isHovered, enabled) => {
    if (!enabled) return {};
    
    if (isSelected) {
        return {
            outline: '2px solid #2196f3',
            outlineOffset: '2px'
        };
    }
    if (isHovered) {
        return {
            outline: '1px dashed #ff9800',
            outlineOffset: '1px'
        };
    }
    return {};
};

// Simple hook to fetch header template only
const useSimpleHeaderTemplate = (sitePagesId, headerTemplate, autoDetectHeader) => {
    const [headerData, setHeaderData] = useState(null);

    useEffect(() => {
        const fetchHeader = async () => {
            if (!autoDetectHeader || !sitePagesId) {
                setHeaderData(null);
                return;
            }

            try {
                const stackItems = await getChildrenItems(sitePagesId);
                const headerItem = stackItems.find(item => {
                    const itemName = item.name || item.title || '';
                    return ['/header', '/nav', '/navbar'].includes(itemName.toLowerCase());
                });
                setHeaderData(headerItem);
            } catch (err) {
                console.error('Error fetching header:', err);
                setHeaderData(null);
            }
        };

        fetchHeader();
    }, [sitePagesId, autoDetectHeader]);

    return headerData;
};


export const Website = ({
    name = "Website",
    // Website identity
    siteTitle = "My Website",
    siteDescription = "A website powered by Qwanyx",
    siteFavicon = "",
    // SEO settings
    enableSEO = true,
    metaKeywords = "",
    ogImage = "",
    googleAnalytics = "",
    // Header template
    sitePagesId = null,
    headerTemplate = null,
    autoDetectHeader = true,
    showHeader = true,
    // Global styling
    theme = "light",
    customCSS = "",
    globalFont = "",
    // Display settings
    width = "100%",
    height = "auto",
    style = {},
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // Fetch header template if enabled
    const headerData = useSimpleHeaderTemplate(sitePagesId, headerTemplate, autoDetectHeader);

    // Listen for navbar route changes to update SEO
    useEffect(() => {
        const handleRouteChange = (event) => {
            if (enableSEO && event.detail) {
                const { route, item } = event.detail;
                
                // Update meta description based on route
                updateMetaDescription(item.label, route);
                
                // Track page view for analytics
                if (googleAnalytics) {
                    trackPageView(route);
                }
            }
        };

        window.addEventListener('navbarRouteChange', handleRouteChange);
        return () => window.removeEventListener('navbarRouteChange', handleRouteChange);
    }, [enableSEO, googleAnalytics]);

    // SEO helper functions
    const updateMetaDescription = (pageTitle, route) => {
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            const newDescription = `${pageTitle} - ${siteDescription}`;
            metaDesc.setAttribute('content', newDescription);
        }
    };

    const trackPageView = (route) => {
        if (window.gtag) {
            window.gtag('config', googleAnalytics, {
                page_path: route
            });
        }
    };

    const containerStyle = {
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: theme === 'dark' ? '#2c2c2c' : '#ffffff',
        color: theme === 'dark' ? '#ffffff' : '#000000',
        fontFamily: globalFont || 'inherit',
        ...style,
        ...getSelectionStyle(isSelected, isHovered, enabled)
    };

    // Create simplified website context for header template
    const websiteContext = {
        siteTitle,
        siteDescription,
        theme,
        enableSEO
    };

    // Helper function to build URLs (simplified for standalone Website component)
    const buildPageUrl = (pagePath) => {
        if (!pagePath.startsWith('/')) {
            pagePath = '/' + pagePath;
        }
        return pagePath; // Simplified - just return the path
    };

    // Simplified Website component focused on SEO and global features
    return (
        <div
            ref={ref => connect(drag(ref))}
            name={name}
            style={containerStyle}
            {...props}
        >
            {/* SEO Meta Tags */}
            {enableSEO && (
                <>
                    <meta name="description" content={siteDescription} />
                    {metaKeywords && <meta name="keywords" content={metaKeywords} />}
                    {ogImage && <meta property="og:image" content={ogImage} />}
                    <meta property="og:title" content={siteTitle} />
                    <meta property="og:description" content={siteDescription} />
                    {siteFavicon && <link rel="icon" href={siteFavicon} />}
                </>
            )}

            {/* Google Analytics */}
            {googleAnalytics && (
                <>
                    <script async src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalytics}`}></script>
                    <script>
                        {`
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${googleAnalytics}');
                        `}
                    </script>
                </>
            )}

            {/* Header Template */}
            {showHeader && headerData && (
                <WebsiteContext.Provider value={websiteContext}>
                    <QwantaProvider _id={headerData._id}>
                        <DNDEditor
                            cardData={typeof headerData.card === 'string' ? JSON.parse(headerData.card) : headerData.card}
                            enabled={false}
                            readOnly={true}
                            compact={false}
                            stackMode={false}
                        />
                    </QwantaProvider>
                </WebsiteContext.Provider>
            )}

            {/* Website Info Panel */}
            <Box sx={{ 
                p: 2, 
                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                margin: '10px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WebsiteIcon color="primary" />
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{siteTitle}</span>
                    {enableSEO && (
                        <Chip 
                            icon={<SEOIcon />} 
                            label="SEO Active" 
                            size="small" 
                            color="success"
                            variant="outlined"
                        />
                    )}
                </Box>
                <Typography variant="body2" color="textSecondary">
                    {siteDescription}
                </Typography>
                
                {headerData && (
                    <Box sx={{ mt: 1, fontSize: '12px', color: 'green' }}>
                        ✅ Header template loaded: {headerData.title}
                    </Box>
                )}
                
                <Box sx={{ mt: 2, fontSize: '12px', color: theme === 'dark' ? '#ccc' : '#666' }}>
                    <strong>How to use:</strong><br/>
                    1. Add a NavBar component to your page<br/>
                    2. Enable routing in NavBar settings<br/>
                    3. Configure menu items with labels and content<br/>
                    4. NavBar will handle navigation and show card content
                </Box>
            </Box>

            {/* Custom CSS */}
            {customCSS && (
                <style>
                    {customCSS}
                </style>
            )}
        </div>
    );
};

const WebsiteSettings = () => {
    const {
        actions: { setProp },
        name,
        siteTitle,
        siteDescription,
        siteFavicon,
        enableSEO,
        metaKeywords,
        ogImage,
        googleAnalytics,
        sitePagesId,
        autoDetectHeader,
        showHeader,
        theme,
        customCSS,
        globalFont,
        width,
        height
    } = useNode((node) => ({
        name: node.data.props.name,
        siteTitle: node.data.props.siteTitle,
        siteDescription: node.data.props.siteDescription,
        siteFavicon: node.data.props.siteFavicon,
        enableSEO: node.data.props.enableSEO,
        metaKeywords: node.data.props.metaKeywords,
        ogImage: node.data.props.ogImage,
        googleAnalytics: node.data.props.googleAnalytics,
        sitePagesId: node.data.props.sitePagesId,
        autoDetectHeader: node.data.props.autoDetectHeader,
        showHeader: node.data.props.showHeader,
        theme: node.data.props.theme,
        customCSS: node.data.props.customCSS,
        globalFont: node.data.props.globalFont,
        width: node.data.props.width,
        height: node.data.props.height
    }));

    return (
        <div>
            {/* Website Identity */}
            <Box sx={{ mb: 2 }}>
                <FormControl fullWidth margin="normal">
                    <FormLabel>Website Name</FormLabel>
                    <TextField
                        value={name || ''}
                        onChange={(e) => setProp(props => props.name = e.target.value)}
                        size="small"
                        placeholder="My Website"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Site Title</FormLabel>
                    <TextField
                        value={siteTitle || ''}
                        onChange={(e) => setProp(props => props.siteTitle = e.target.value)}
                        size="small"
                        placeholder="My Awesome Website"
                        helperText="Used in page titles and SEO"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Site Description</FormLabel>
                    <TextField
                        value={siteDescription || ''}
                        onChange={(e) => setProp(props => props.siteDescription = e.target.value)}
                        size="small"
                        multiline
                        rows={2}
                        placeholder="A brief description of your website"
                        helperText="Used for SEO meta description"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Favicon URL</FormLabel>
                    <TextField
                        value={siteFavicon || ''}
                        onChange={(e) => setProp(props => props.siteFavicon = e.target.value)}
                        size="small"
                        placeholder="https://example.com/favicon.ico"
                        helperText="Website icon shown in browser tabs"
                    />
                </FormControl>
            </Box>

            {/* SEO Settings */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    SEO Configuration
                </FormLabel>

                <FormControlLabel
                    control={
                        <Switch
                            checked={enableSEO || false}
                            onChange={(e) => setProp(props => props.enableSEO = e.target.checked)}
                        />
                    }
                    label="Enable SEO Features"
                />

                {enableSEO && (
                    <>
                        <FormControl fullWidth margin="normal">
                            <FormLabel>Meta Keywords</FormLabel>
                            <TextField
                                value={metaKeywords || ''}
                                onChange={(e) => setProp(props => props.metaKeywords = e.target.value)}
                                size="small"
                                placeholder="website, business, service"
                                helperText="Comma-separated keywords"
                            />
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <FormLabel>Open Graph Image URL</FormLabel>
                            <TextField
                                value={ogImage || ''}
                                onChange={(e) => setProp(props => props.ogImage = e.target.value)}
                                size="small"
                                placeholder="https://example.com/og-image.jpg"
                                helperText="Image for social media sharing"
                            />
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <FormLabel>Google Analytics ID</FormLabel>
                            <TextField
                                value={googleAnalytics || ''}
                                onChange={(e) => setProp(props => props.googleAnalytics = e.target.value)}
                                size="small"
                                placeholder="G-XXXXXXXXXX"
                                helperText="Google Analytics tracking ID"
                            />
                        </FormControl>
                    </>
                )}
            </Box>

            {/* Header Template */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Header Template
                </FormLabel>

                <FormControlLabel
                    control={
                        <Switch
                            checked={showHeader || false}
                            onChange={(e) => setProp(props => props.showHeader = e.target.checked)}
                        />
                    }
                    label="Show Header"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={autoDetectHeader || false}
                            onChange={(e) => setProp(props => props.autoDetectHeader = e.target.checked)}
                        />
                    }
                    label="Auto-detect Header (/header, /nav, /navbar)"
                />

                <FormControl fullWidth margin="normal">
                    <FormLabel>Header Stack ID</FormLabel>
                    <TextField
                        value={sitePagesId || ''}
                        onChange={(e) => setProp(props => props.sitePagesId = e.target.value)}
                        size="small"
                        placeholder="Enter stack ID containing header items"
                        helperText="Stack containing your header template"
                    />
                </FormControl>
            </Box>

            {/* Global Styling */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Global Styling
                </FormLabel>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Theme</FormLabel>
                    <Select
                        value={theme || "light"}
                        onChange={(e) => setProp(props => props.theme = e.target.value)}
                        size="small"
                    >
                        <MenuItem value="light">Light Theme</MenuItem>
                        <MenuItem value="dark">Dark Theme</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Global Font Family</FormLabel>
                    <TextField
                        value={globalFont || ''}
                        onChange={(e) => setProp(props => props.globalFont = e.target.value)}
                        size="small"
                        placeholder="Arial, Helvetica, sans-serif"
                        helperText="CSS font-family for entire website"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Custom CSS</FormLabel>
                    <TextField
                        value={customCSS || ''}
                        onChange={(e) => setProp(props => props.customCSS = e.target.value)}
                        size="small"
                        multiline
                        rows={3}
                        placeholder="body { background-color: #f5f5f5; }"
                        helperText="Global CSS styles for your website"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Website Width</FormLabel>
                    <TextField
                        value={width || '100%'}
                        onChange={(e) => setProp(props => props.width = e.target.value)}
                        size="small"
                        placeholder="100%, 1200px, 80vw"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Website Height</FormLabel>
                    <TextField
                        value={height || 'auto'}
                        onChange={(e) => setProp(props => props.height = e.target.value)}
                        size="small"
                        placeholder="auto, 600px, 100vh"
                    />
                </FormControl>
            </Box>
        </div>
    );
};

Website.craft = {
    props: {
        name: "Website",
        // Website identity
        siteTitle: "My Website",
        siteDescription: "A website powered by Qwanyx",
        siteFavicon: "",
        // SEO settings
        enableSEO: true,
        metaKeywords: "",
        ogImage: "",
        googleAnalytics: "",
        // Header template
        sitePagesId: null,
        headerTemplate: null,
        autoDetectHeader: true,
        showHeader: true,
        // Global styling
        theme: "light",
        customCSS: "",
        globalFont: "",
        // Display settings
        width: "100%",
        height: "auto"
    },
    rules: {
        canDrag: true
    },
    related: {
        settings: WebsiteSettings
    }
};